import { useEffect, useState } from "react";
import { Loader2, Plus, Check, X } from "lucide-react";
import api from "@/lib/api";
import { CRM_STAGES, CRM_CONTACT_TYPES, isEU } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";
import { CrmSheet } from "@/components/admin/CrmSheet";

const STAGE_CLS = {
  to_contact: "text-zinc-400 border-zinc-600/50",
  contacted: "text-blue-300 border-blue-500/40",
  responded: "text-cyan-300 border-cyan-500/40",
  deal: "text-violet-300 border-violet-500/40",
  shipped: "text-amber-300 border-amber-500/40",
  published: "text-emerald-300 border-emerald-500/40",
  done: "text-emerald-400 border-emerald-500/50",
  not_interested: "text-red-300 border-red-500/40",
};

const TYPE_CLS = {
  creator: "text-fuchsia-300 border-fuchsia-500/40",
  customer: "text-sky-300 border-sky-500/40",
  store: "text-amber-300 border-amber-500/40",
};

const EMPTY_LEAD = { name: "", contact_type: "creator", platform: "instagram", stage: "to_contact", responded: false, shipped: false, content_links: [] };

export function CrmTable() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: leads }, { data: st }] = await Promise.all([
        api.get("/admin/crm/leads"), api.get("/admin/crm/stats"),
      ]);
      setRows(leads); setStats(st);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openLead = (lead) => { setSel(lead); setOpen(true); };
  const openNew = () => { setSel({ ...EMPTY_LEAD }); setOpen(true); };

  const onSaved = (data, isNew) => {
    setRows((arr) => isNew ? [data, ...arr] : arr.map((r) => (r.id === data.id ? { ...r, ...data } : r)));
    api.get("/admin/crm/stats").then(({ data: st }) => setStats(st)).catch(() => {});
    if (isNew) setOpen(false);
  };
  const onDeleted = (id) => { setRows((arr) => arr.filter((r) => r.id !== id)); setOpen(false); api.get("/admin/crm/stats").then(({ data: st }) => setStats(st)).catch(() => {}); };

  const shown = rows.filter((r) => (!filter || r.stage === filter) && (!typeFilter || (r.contact_type || "creator") === typeFilter));

  const STAT_ITEMS = [
    ["total", stats?.total], ["contacted", stats?.contacted], ["responded", stats?.responded],
    ["shipped", stats?.shipped], ["published", stats?.published], ["converted", stats?.converted],
  ];

  return (
    <div data-testid="crm-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">{t("crm.title")}</h2>
          <p className="text-sm text-zinc-500 max-w-2xl">{t("crm.subtitle")}</p>
        </div>
        <button data-testid="crm-new-btn" onClick={openNew} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold text-sm px-5 py-2.5 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform]">
          <Plus size={16} /> {t("crm.new")}
        </button>
      </div>

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {STAT_ITEMS.map(([k, v]) => (
          <div key={k} className="rounded-xl border border-aura-border bg-aura-surface p-4">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{t(`crm.stats.${k}`)}</div>
            <div className="text-2xl font-display font-bold text-white mt-1">{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs uppercase tracking-wide text-zinc-500 me-1">{t("crm.contactType")}:</span>
        <button data-testid="crm-type-all" onClick={() => setTypeFilter("")} className={`rounded-full border px-3 py-1 text-xs transition-colors ${typeFilter === "" ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{t("crm.allTypes")}</button>
        {CRM_CONTACT_TYPES.map((tp) => (
          <button key={tp} data-testid={`crm-type-${tp}`} onClick={() => setTypeFilter(tp)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${typeFilter === tp ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
            {t(`crm.types.${tp}`)}{stats?.by_type ? ` (${stats.by_type[tp] ?? 0})` : ""}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter("")} className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === "" ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>Tutti</button>
        {CRM_STAGES.map((s) => (
          <button key={s} data-testid={`crm-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === s ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{t(`crm.stages.${s}`)}</button>
        ))}
      </div>

      <div className="rounded-xl border border-aura-border bg-aura-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium text-start">{t("crm.colName")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colType")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colPlatform")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colCountry")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colStage")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colResponded")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colShipped")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colViews")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colSales")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("crm.colInvited")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-zinc-500">{t("crm.empty")}</td></tr>
              ) : shown.map((r) => (
                <tr key={r.id} data-testid={`crm-row-${r.id}`} onClick={() => openLead(r)} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors">
                  <td className="px-4 py-4 text-white">{r.name}{r.converted_creator_id && <span className="ml-2 text-[10px] text-emerald-400">★ creator</span>}</td>
                  <td className="px-4 py-4"><span className={`inline-flex rounded-full border ${TYPE_CLS[r.contact_type || "creator"] || ""} bg-white/[0.03] px-2.5 py-1 text-xs`}>{t(`crm.types.${r.contact_type || "creator"}`)}</span></td>
                  <td className="px-4 py-4 text-zinc-300">{t(`crm.platforms.${r.platform}`)}</td>
                  <td className="px-4 py-4">
                    {r.country ? (
                      <span className="inline-flex items-center gap-1.5 text-zinc-300" title={isEU(r.country) ? "UE" : "Extra-UE — dogana"}>
                        <span className={`h-1.5 w-1.5 rounded-full ${isEU(r.country) ? "bg-sky-400" : "bg-amber-400"}`} />
                        {r.country}
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-4"><span className={`inline-flex rounded-full border ${STAGE_CLS[r.stage] || ""} bg-white/[0.03] px-2.5 py-1 text-xs`}>{t(`crm.stages.${r.stage}`)}</span></td>
                  <td className="px-4 py-4">{r.responded ? <Check size={15} className="text-emerald-400" /> : <X size={15} className="text-zinc-600" />}</td>
                  <td className="px-4 py-4">{r.shipped ? <Check size={15} className="text-emerald-400" /> : <X size={15} className="text-zinc-600" />}</td>
                  <td className="px-4 py-4 text-zinc-300">{(r.result_views || 0).toLocaleString()}</td>
                  <td className="px-4 py-4 text-zinc-300">€{(r.result_sales || 0).toLocaleString()}</td>
                  <td className="px-4 py-4">{r.invite_email_sent ? <Check size={15} className="text-emerald-400" /> : <X size={15} className="text-zinc-600" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CrmSheet lead={sel} open={open} onOpenChange={setOpen} onSaved={onSaved} onDeleted={onDeleted} />
    </div>
  );
}
